// need this import to load polyfill;
import '@formatjs/intl-relativetimeformat/polyfill.js';
import '@formatjs/intl-relativetimeformat/dist/locale-data/en.js';

import { type Unit } from '@formatjs/intl-relativetimeformat';
import { type IntlShape } from 'dibs-react-intl';

export type RelativeMode = 'precise' | 'calendar';

const second = 1000;
const minute = second * 60;
const hour = minute * 60;
const day = hour * 24;

const thresholds = {
    second: 45, // seconds to minute
    minute: 45, // minutes to hour
    hour: 22, // hour to day
    day: 5, // day to week
};

function selectUnitPrecise(dateOrString: Date | string): { value: number; unit: Unit } {
    const date = typeof dateOrString === 'string' ? new Date(dateOrString) : dateOrString;
    const delta = date.getTime() - new Date().getTime();

    const secs = delta / second;
    if (Math.abs(secs) < thresholds.second) {
        return {
            value: Math.round(secs),
            unit: 'second',
        };
    }

    const mins = delta / minute;
    if (Math.abs(mins) < thresholds.minute) {
        return {
            value: Math.round(mins),
            unit: 'minute',
        };
    }

    const hours = delta / hour;
    if (Math.abs(hours) < thresholds.hour) {
        return {
            value: Math.round(hours),
            unit: 'hour',
        };
    }

    const days = delta / day;
    return {
        value: Math.round(days),
        unit: 'day',
    };
}

function toDate(input: Date | string): Date {
    return typeof input === 'string' ? new Date(input) : input;
}

function startOfLocalDay(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function selectUnitCalendar(dateOrString: Date | string): { value: number; unit: Unit } {
    const date = startOfLocalDay(toDate(dateOrString));
    const now = startOfLocalDay(new Date());

    const diffDays = Math.round((date.getTime() - now.getTime()) / day);

    // Today / Yesterday / X days
    if (Math.abs(diffDays) < 7) {
        return { value: diffDays, unit: 'day' };
    }

    const diffWeeks = Math.round(diffDays / 7);
    // Last week / X weeks
    if (Math.abs(diffWeeks) < 4) {
        return { value: diffWeeks, unit: 'week' };
    }

    const monthsNow = now.getFullYear() * 12 + now.getMonth();
    const monthsDate = date.getFullYear() * 12 + date.getMonth();
    const diffMonths = monthsDate - monthsNow;

    // X months ago
    if (Math.abs(diffMonths) < 12) {
        return { value: diffMonths, unit: 'month' };
    }

    // X years ago
    const diffYears = date.getFullYear() - now.getFullYear();
    return { value: diffYears, unit: 'year' };
}

export const formatRelativeDate = function formatRelativeDate(
    intl: IntlShape,
    dateOrString: Date | string,
    options?: { mode?: RelativeMode }
): string {
    const mode: RelativeMode = options?.mode ?? 'precise';

    const { value, unit } =
        mode === 'calendar' ? selectUnitCalendar(dateOrString) : selectUnitPrecise(dateOrString);

    return new Intl.RelativeTimeFormat(intl.locale, { numeric: 'auto' }).format(value, unit);
};
